import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ukDay, ukTime } from "@/lib/time";
import { PageHead } from "@/components/hub/Shell";
import { Panel, Chips, Empty } from "@/components/portal/crm/kit";
import { ConnieMark } from "@/components/connie/ConnieMark";
import { ConnieText } from "@/components/connie/ConnieText";
import { ReplyToPartner } from "@/components/hub/ReplyToPartner";

const TONE = { open: "st-sent", triaged: "st-rev", resolved: "st-off", wontfix: "st-neutral" };
const STATUSES = [
  { key: "open", label: "New" }, { key: "triaged", label: "Looking at it" },
  { key: "resolved", label: "Done" }, { key: "wontfix", label: "Not for now" },
];
const CATS = [
  { key: "all", label: "Everything" }, { key: "idea", label: "Ideas" }, { key: "change", label: "Changes" },
  { key: "issue", label: "Something's wrong" }, { key: "tech", label: "Tech" }, { key: "praise", label: "Praise" },
  { key: "request", label: "Requests" }, { key: "complaint", label: "Complaints" },
];
const ROLE = { lender: "Lender", referrer: "Introducer", admin: "Connexis" };

/** Partner feedback — everything from Help shape Connexis, plus everything Connie passed on. */
export default function HubFeedback() {
  const [status, setStatus] = useState("open");
  const [cat, setCat] = useState("all");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { setData((await api.get("/hub/feedback", { params: { status, category: cat } })).data); }
    catch { setData({ items: [], counts: {} }); }
  }, [status, cat]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="hub-feedback">
      <PageHead eyebrow="Help shape Connexis" title="Partner feedback" testId="hub-feedback-head"
        sub="Ideas, change requests, problems and praise from lenders, introducers, call centres and media partners — and everything Connie passed on to you." />
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
        <Chips testPrefix="hub-feedback-status" value={status} onChange={setStatus}
               items={[...STATUSES.map((s) => ({ ...s, count: data?.counts?.[s.key] || 0 })), { key: "all", label: "All" }]} />
        <Chips testPrefix="hub-feedback-cat" value={cat} onChange={setCat} items={CATS} />
      </div>
      {!data ? <Empty>Loading…</Empty> : data.items.length === 0 ? (
        <Panel testId="hub-feedback-empty"><Empty>Nothing here. When partners speak up, it lands on this page.</Empty></Panel>
      ) : (
        <div className="space-y-3.5">{data.items.map((f) => <Item key={f.id} f={f} onDone={load} />)}</div>
      )}
    </div>
  );
}

function Item({ f, onDone }) {
  const [saving, setSaving] = useState(false);
  const set = async (st) => {
    setSaving(true);
    try {
      await api.patch(`/hub/feedback/${f.id}`, { status: st, reply_note: null });
      toast.success("Updated");
      onDone();
    } catch { toast.error("That didn't save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="card" style={{ padding: "18px 22px" }} data-testid={`hub-feedback-item-${f.id}`}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
        <span className={`st ${TONE[f.status] || "st-neutral"}`}><i className="sq" />{f.status_label}</span>
        <b style={{ fontSize: 13 }}>{f.category_label}</b>
        {f.urgent && <span className="st st-dec"><i className="sq" />Urgent</span>}
        {f.source === "connie" && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--accent-text)", fontWeight: 700 }}>
            <ConnieMark size={18} orbit={false} /> Passed on by Connie
          </span>
        )}
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--mut)" }}>{ukDay(f.created_at)} · {ukTime(f.created_at)}</span>
      </div>
      <div style={{ fontSize: 12.5, color: "var(--mut)", marginTop: 8 }} data-testid={`hub-feedback-from-${f.id}`}>
        {f.contact_name || "Someone"}{f.party_name && f.party_name !== f.contact_name ? ` · ${f.party_name}` : ""} · {ROLE[f.user_role] || f.partner_label || "Partner"}
        {f.partner_label && f.user_role === "referrer" ? ` (${f.partner_label})` : ""}{f.user_email ? ` · ${f.user_email}` : ""}
      </div>
      <p style={{ margin: "10px 0 0", fontSize: 14, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{f.description}</p>
      {f.connie_answer && (
        <div className="connie-bubble" style={{ marginTop: 12, fontSize: 13 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--accent-text)", marginBottom: 6 }}>Connie told them</div>
          <ConnieText text={f.connie_answer} />
        </div>
      )}
      {f.reply_note && <div style={{ marginTop: 10, fontSize: 13, color: "var(--ink-3)" }} data-testid={`hub-feedback-replied-${f.id}`}><b>You replied:</b> {f.reply_note}</div>}
      <ReplyToPartner feedbackId={f.id} testId={`hub-feedback-reply-${f.id}`} onSent={onDone}>
        {STATUSES.filter((s) => s.key !== f.status).map((s) => (
          <button key={s.key} className="btn btn-ghost btn-sm" disabled={saving} onClick={() => set(s.key)}
                  data-testid={`hub-feedback-set-${s.key}-${f.id}`}>{s.label}</button>
        ))}
      </ReplyToPartner>
    </div>
  );
}
